import type { ReactNode } from 'react';
import {
  CategoryBarChart,
  ChartCard,
  DailySeriesChart,
  formatCount,
  formatUsd,
  CHART_SEMANTIC,
  metricInfo,
  MONEY,
} from '@kioskgaming/ui';
import MetricStrip from './MetricStrip';
import { channelMixFromKpis } from './chartHelpers';
import type { DashboardV2Period, SuperAgentDashboardFinancialData } from '../../services/api';

type Props = {
  period: DashboardV2Period;
  data: SuperAgentDashboardFinancialData | null;
  isLoading: boolean;
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-2">
    <h2 className="text-xs font-semibold uppercase tracking-wide text-blue-900/70">{title}</h2>
    {children}
  </section>
);

/**
 * KIOSK-423 — catalog Finance names without duplicate totals:
 * Credits Sold / Credit Cost / Credit Margin map 1:1 to earnedTotal / spentTotal /
 * netPosition (same USD formulas). Breakdown strips are components of those totals.
 * Credits Withdrawn Back = catalog net-returned (KIOSK-421).
 */
export default function FinancialTab({ data, isLoading }: Props) {
  const kpis = data?.kpis;
  const sold = metricInfo('creditsSold');
  const cost = metricInfo('creditCost');
  const margin = metricInfo('creditMargin');
  const withdrawnBack = metricInfo('creditsWithdrawnBack');
  const earnedMix = (data?.charts?.earnedMix ?? []).filter((d) => d.value > 0);
  const dailyNet = data?.charts?.dailyNetPosition ?? [];

  const moneyInByChannel = channelMixFromKpis([
    { name: 'Online deposits', kpi: kpis?.onlineDepositsNetUsd },
    { name: 'Counter deposits', kpi: kpis?.counterDepositsCashReceived },
  ]);

  const payoutsByChannel = channelMixFromKpis([
    { name: 'Online withdrawals', kpi: kpis?.onlineWithdrawalsGrossUsd },
    { name: 'Counter redemptions', kpi: kpis?.counterRedemptionsCashPaid },
  ]);

  return (
    <div className="space-y-4">
      <MetricStrip
        title="Performance"
        columnsClassName="grid-cols-2 sm:grid-cols-4"
        items={[
          {
            id: 'creditMargin',
            label: margin.label,
            tooltip:
              'Credits sold minus credit cost for the selected period (same as your trading P&L: earned − spent).',
            kpi: kpis?.netPosition,
            format: 'currency',
            emphasis: true,
            barClassName: 'bg-violet-100',
            valueClassName: 'text-violet-900',
          },
          {
            id: 'creditsSold',
            label: sold.label,
            tooltip:
              'USD you earned from credit sales to your Agents this period (cash + online + balance converts). Equals Cash from Agents + Credits sold online + Agent converts — not a second total.',
            kpi: kpis?.earnedTotal,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
          {
            id: 'creditCost',
            label: cost.label,
            tooltip:
              `USD you paid the Platform for credits this period (online and balance-convert purchases, plus cash). Equals ${MONEY.creditsBought} + Cash paid to the Platform — not a second total.`,
            kpi: kpis?.spentTotal,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
          {
            id: 'creditsWithdrawnBack',
            label: withdrawnBack.label,
            tooltip: withdrawnBack.tooltip,
            kpi: kpis?.creditsWithdrawnBack,
            format: 'number',
            barClassName: 'bg-amber-100',
            valueClassName: 'text-amber-800',
          },
        ]}
      />

      <MetricStrip
        title="Credits sold breakdown"
        columnsClassName="grid-cols-2 sm:grid-cols-3"
        items={[
          {
            id: 'cashAgents',
            label: 'Cash from Agents',
            tooltip: 'Component of credits sold — cash your Agents paid you for credits.',
            kpi: kpis?.cashFromAgents,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
          {
            id: 'cdn',
            label: 'Credits sold online',
            tooltip: 'Component of credits sold — USD from Agents buying credits online.',
            kpi: kpis?.cdnSoldToAgents,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
          {
            id: 'converts',
            label: 'Agent converts',
            tooltip: 'Component of credits sold — USD from Agents converting balance into credits.',
            kpi: kpis?.convertsFromAgents,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
        ]}
      />

      <MetricStrip
        title="Credit cost breakdown"
        columnsClassName="grid-cols-2 sm:grid-cols-2"
        items={[
          {
            id: 'creditsWithdrawnBack',
            label: withdrawnBack.label,
            tooltip: withdrawnBack.tooltip,
            kpi: kpis?.creditsWithdrawnBack,
            format: 'number',
            barClassName: 'bg-amber-100',
            valueClassName: 'text-amber-800',
          },
          {
            id: 'purchased',
            label: MONEY.creditsBought,
            tooltip: 'Component of credit cost — USD you paid the Platform for credits (online and balance-convert purchases).',
            kpi: kpis?.creditsPurchasedUsd,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
          {
            id: 'cashPaidPlatform',
            label: 'Cash paid to the Platform',
            tooltip: 'Component of credit cost — cash you paid the Platform for credit purchases.',
            kpi: kpis?.cashPaidUpstream,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
        ]}
      />

      <MetricStrip
        title="Network Player money"
        columnsClassName="grid-cols-2 sm:grid-cols-3 xl:grid-cols-6"
        items={[
          {
            id: 'playerIn',
            label: 'Player money in',
            tooltip: 'Online deposits plus counter cash from Shop-linked Players.',
            kpi: kpis?.playerMoneyIn,
            format: 'currency',
            barClassName: 'bg-blue-100',
          },
          {
            id: 'onlineDep',
            label: 'Online deposits',
            tooltip: 'Net USD from online Player deposits.',
            kpi: kpis?.onlineDepositsNetUsd,
            format: 'currency',
            barClassName: 'bg-emerald-100',
            valueClassName: 'text-emerald-800',
          },
          {
            id: 'onlineWd',
            label: 'Online withdrawals',
            tooltip: 'Gross USD paid out to Players online.',
            kpi: kpis?.onlineWithdrawalsGrossUsd,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
          {
            id: 'counterDep',
            label: 'Counter deposits',
            tooltip: 'Cash received at Shop counters.',
            kpi: kpis?.counterDepositsCashReceived,
            format: 'currency',
            barClassName: 'bg-blue-100',
          },
          {
            id: 'counterRdm',
            label: 'Counter redemptions',
            tooltip: 'Cash paid out at Shop counters.',
            kpi: kpis?.counterRedemptionsCashPaid,
            format: 'currency',
            invertDelta: true,
            barClassName: 'bg-orange-100',
            valueClassName: 'text-orange-800',
          },
          {
            id: 'funding',
            label: 'Payout funding',
            tooltip: 'USD your network spent funding Player online withdrawals.',
            kpi: kpis?.tierFundingUsd,
            format: 'currency',
            barClassName: 'bg-blue-100',
          },
        ]}
      />
      <p className="text-xs text-muted-foreground">
        {formatCount(kpis?.onlineDepositsCount?.value ?? 0)} online deposits ·{' '}
        {formatCount(kpis?.onlineWithdrawalsCount?.value ?? 0)} online withdrawals
      </p>

      <Section title="Trends">
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <ChartCard
            title="Daily credit margin"
            tooltip="Daily credits sold minus credit cost (earned − spent)."
            loading={isLoading}
            empty={!isLoading && dailyNet.every((d) => d.net === 0)}
          >
            <div className="h-64">
              <DailySeriesChart
                data={dailyNet}
                xKey="date"
                height={256}
                series={[
                  { key: 'earned', label: MONEY.creditsSold, color: CHART_SEMANTIC.moneyIn },
                  { key: 'spent', label: 'Credit cost', color: CHART_SEMANTIC.moneyOut },
                  { key: 'net', label: 'Credit margin', color: CHART_SEMANTIC.profit },
                ]}
                valueFormatter={formatUsd}
              />
            </div>
          </ChartCard>
          <ChartCard
            title="Credits sold mix"
            tooltip="How credits sold broke down this period (cash / online / converts)."
            loading={isLoading}
            empty={!isLoading && earnedMix.length === 0}
            emptyLabel="No credit sales in this period"
          >
            <CategoryBarChart data={earnedMix} color={CHART_SEMANTIC.moneyIn} valueFormatter={formatUsd} />
          </ChartCard>
          <ChartCard
            title="Money in by channel"
            tooltip="Player money into your network this period: online deposits vs counter cash."
            loading={isLoading}
            empty={!isLoading && moneyInByChannel.length === 0}
            emptyLabel="No Player money in this period"
          >
            <CategoryBarChart
              data={moneyInByChannel}
              color={CHART_SEMANTIC.moneyIn}
              valueFormatter={formatUsd}
            />
          </ChartCard>
          <ChartCard
            title="Payouts by channel"
            tooltip="Money leaving your network this period: online withdrawals vs counter redemptions."
            loading={isLoading}
            empty={!isLoading && payoutsByChannel.length === 0}
            emptyLabel="No payouts in this period"
          >
            <CategoryBarChart
              data={payoutsByChannel}
              color={CHART_SEMANTIC.moneyOut}
              valueFormatter={formatUsd}
            />
          </ChartCard>
        </div>
      </Section>
    </div>
  );
}
